import { useCallback, useEffect, useReducer, useRef } from 'react'
import type { Socket } from 'socket.io-client'
import { createWebchatSocket } from '~/components/Webchat/webchatSocket'
import {
  initialWebchatState,
  webchatReducer,
} from '~/components/Webchat/webchatReducer'
import type {
  ChatMessage,
  ConnStatus,
  IdentifyAck,
  OutMessage,
  SendAck,
  SessionState,
} from '~/components/Webchat/types'
import { markChatLeadOnce } from '~/components/Webchat/webchatLead'
import { trackChatLead } from '~/utils/analytics'

const TOKEN_KEY = 'webchat_token'
const MAX_LEN = 4000

const readToken = (): string | undefined => {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? undefined
  } catch {
    return undefined
  }
}
const writeToken = (t: string) => {
  try {
    localStorage.setItem(TOKEN_KEY, t)
  } catch {
    /* ignore */
  }
}

export type UseWebchat = {
  messages: ChatMessage[]
  assignedName: string | null
  connection: ConnStatus
  sessionState: SessionState
  unreadCount: number
  panelOpen: boolean
  terminalError: string | null
  openPanel: () => void
  closePanel: () => void
  send: (text: string) => void
  identify: (payload: { name?: string; contact?: string }) => void
  /** Inject a local operator message (e.g. proactive "need help?"); auto-opens. */
  injectAdminMessage: (text: string) => void
}

export type UseWebchatArgs = {
  locale: string // 'ru' | 'ro'
  path: string // current page, locale-stripped (e.g. /configurator)
}

export function useWebchat({ locale, path }: UseWebchatArgs): UseWebchat {
  const [state, dispatch] = useReducer(webchatReducer, initialWebchatState)
  const socketRef = useRef<Socket | null>(null)
  const leadFired = useRef(false)

  // Latest locale/path, read by connect/reconnect handlers (which capture the
  // mount-time closure) so resumed sessions always carry current context.
  const localeRef = useRef(locale)
  localeRef.current = locale
  const pathRef = useRef(path)
  pathRef.current = path

  const buildAuth = () => {
    const token = readToken()
    return {
      ...(token ? { token } : {}),
      locale: localeRef.current,
      path: pathRef.current,
    }
  }

  useEffect(() => {
    // StrictMode guard: a stale (double-mounted) socket must not write state.
    let active = true
    const socket = createWebchatSocket(buildAuth())
    socketRef.current = socket

    const onConnect = () => {
      if (!active) return
      dispatch({ type: 'CONNECTED' })
      // Explicit context on every (re)connect — gives the backend a hook to
      // surface the visitor + page in Telegram on entry, without waiting for
      // the first chat message. Fire-and-forget (acks { ok } | { error }).
      socket.emit('context', {
        path: pathRef.current,
        locale: localeRef.current,
      })
    }
    const onReconnectAttempt = () => {
      // Re-handshake with the latest token (maybe written by another tab) plus
      // current locale/path, so resumed sessions keep their context.
      socket.auth = buildAuth()
      if (active) dispatch({ type: 'RECONNECTING' })
    }
    const onDisconnect = () => {
      if (active) dispatch({ type: 'DISCONNECTED' })
    }
    const onSession = (p: { token: string; name: string }) => {
      if (!active) return
      writeToken(p.token)
      socket.auth = { ...buildAuth(), token: p.token } // reconnects use the real token
      dispatch({ type: 'SESSION', name: p.name })
    }
    const onHistory = (p: { messages: OutMessage[]; state: SessionState }) => {
      if (active)
        dispatch({ type: 'HISTORY', messages: p.messages, state: p.state })
    }
    const onMessage = (m: OutMessage) => {
      if (active)
        dispatch({ type: 'INBOUND', key: crypto.randomUUID(), msg: m })
    }
    const onError = (e: { message: string }) => {
      if (!active) return
      dispatch({ type: 'TERMINAL_ERROR', message: e.message })
      socket.io.reconnection(false) // do not loop on terminal error
    }
    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== TOKEN_KEY || !ev.newValue) return
      socket.auth = { ...buildAuth(), token: ev.newValue }
      socket.disconnect().connect()
    }

    dispatch({ type: 'CONNECTING' })
    socket.on('connect', onConnect)
    socket.io.on('reconnect_attempt', onReconnectAttempt)
    socket.on('disconnect', onDisconnect)
    socket.on('session', onSession)
    socket.on('history', onHistory)
    socket.on('message', onMessage)
    socket.on('error', onError)
    window.addEventListener('storage', onStorage)

    return () => {
      active = false
      window.removeEventListener('storage', onStorage)
      socket.off('connect', onConnect)
      socket.io.off('reconnect_attempt', onReconnectAttempt)
      socket.off('disconnect', onDisconnect)
      socket.off('session', onSession)
      socket.off('history', onHistory)
      socket.off('message', onMessage)
      socket.off('error', onError)
      socket.disconnect()
      socketRef.current = null
    }
  }, [])

  // Tell the backend when the visitor navigates or switches language. The
  // initial values already ride on the connect auth, so skip the first run.
  // Debounced so rapid client-side redirects don't burn the shared rate limit.
  const firstContext = useRef(true)
  useEffect(() => {
    if (firstContext.current) {
      firstContext.current = false
      return
    }
    const id = setTimeout(() => {
      // Fire-and-forget; backend dedupes and acks { ok } | { error }.
      socketRef.current?.emit('context', {
        path: pathRef.current,
        locale: localeRef.current,
      })
    }, 400)
    return () => clearTimeout(id)
  }, [locale, path])

  const send = (raw: string) => {
    const text = raw.trim().slice(0, MAX_LEN)
    const socket = socketRef.current
    if (!text || !socket) return
    const clientTempId = crypto.randomUUID()
    dispatch({
      type: 'SEND',
      clientTempId,
      text,
      at: new Date().toISOString(),
    })
    socket.emit('message', { text }, (ack: SendAck) => {
      if ('error' in ack) {
        dispatch({ type: 'ACK_ERR', clientTempId, error: ack.error })
      } else {
        // Lead fires only on a confirmed delivery, not on the send attempt.
        if (markChatLeadOnce(leadFired)) {
          trackChatLead({ pagePath: pathRef.current })
        }
        dispatch({
          type: 'ACK_OK',
          clientTempId,
          id: ack.id,
          status: ack.status,
        })
      }
    })
  }

  const identify = (payload: { name?: string; contact?: string }) => {
    socketRef.current?.emit('identify', payload, (_ack: IdentifyAck) => {})
  }

  const openPanel = useCallback(() => dispatch({ type: 'OPEN_PANEL' }), [])
  const closePanel = useCallback(() => dispatch({ type: 'CLOSE_PANEL' }), [])

  // Local, non-persisted operator message. INBOUND 'admin' auto-opens the panel
  // (see reducer); dropped on the next HISTORY load since the backend never saw
  // it. Used for the client-side proactive prompt until the backend owns it.
  const injectAdminMessage = useCallback((text: string) => {
    dispatch({
      type: 'INBOUND',
      key: crypto.randomUUID(),
      msg: { from: 'admin', text, at: new Date().toISOString() },
    })
  }, [])

  return {
    messages: state.messages,
    assignedName: state.assignedName,
    connection: state.connection,
    sessionState: state.sessionState,
    unreadCount: state.unreadCount,
    panelOpen: state.panelOpen,
    terminalError: state.terminalError,
    openPanel,
    closePanel,
    send,
    identify,
    injectAdminMessage,
  }
}
